import { Injectable } from '@angular/core';
import { loadTranslations } from '@angular/localize';
import { firstValueFrom } from 'rxjs';
import { I18NService } from '../../../api/services/i-18-n.service';

// a DB-ben/adminban a fordítók a régi {{paramName}} mustache-jelölést és az ng extract-i18n
// {$PARAMNAME} jelölését is láthatják; a $localize/loadTranslations() a {$NAME} formát várja,
// kis- és nagybetű-érzékeny névegyezéssel
const PARAM_PATTERN = /\{\{\s*(\w+)\s*\}\}|\{\$(\w+)\}/g;

@Injectable({
  providedIn: 'root',
})
export class TranslationRuntimeService {
  private rawDictionary: Record<string, string> = {};
  private loadedLangCode?: string;
  private loadedForToken?: string;

  constructor(private i18nService: I18NService) {}

  get loaded(): boolean {
    return this.loadedLangCode !== undefined;
  }

  // a backend bejelentkezés nélkül csak az Unauthorized scope-ú szövegeket adja vissza, ezért
  // login/logout után (más token) a szótárat újra kell tölteni
  isLoadedFor(langCode: string, token: string | undefined): boolean {
    return this.loadedLangCode === langCode && this.loadedForToken === token;
  }

  get currentLangCode(): string | undefined {
    return this.loadedLangCode;
  }

  // a token csak annak rögzítésére szolgál, milyen bejelentkezési állapotban töltöttük be a
  // szótárat (a kérés fejlécét az interceptor állítja be)
  async load(langCode: string, token?: string): Promise<void> {
    const dictionary = await firstValueFrom(this.i18nService.getTranslations({ langCode }));
    this.rawDictionary = dictionary ?? {};

    const translations: Record<string, string> = {};
    for (const key of Object.keys(this.rawDictionary)) {
      translations[key] = this.toLocalizePlaceholders(this.rawDictionary[key]);
    }

    loadTranslations(translations);
    $localize.locale = langCode;

    this.loadedLangCode = langCode;
    this.loadedForToken = token;
  }

  // dinamikus/összefűzött kulcsokhoz (lásd DictionaryLookupService) - a nyers, még
  // {{paramName}} formátumú szöveget adja vissza, ugyanazt a fallback-logikát követve,
  // mint a mai CustomTranslateParser (target || hint || key). Ha a kulcs maga is
  // 'kulcs|hint' formátumú (pl. futásidejű adatstruktúrákból, mint a side-nav menü
  // csomópontok name mezője), a beágyazott hint-et is figyelembe veszi.
  getRaw(key: string, fallback?: string): string {
    const separatorIndex = key.indexOf('|');
    let embeddedHint: string | undefined;
    let actualKey = key;
    if (separatorIndex >= 0) {
      embeddedHint = key.substring(separatorIndex + 1);
      actualKey = key.substring(0, separatorIndex);
    }
    return this.rawDictionary[actualKey] || fallback || embeddedHint || actualKey;
  }

  private toLocalizePlaceholders(text: string): string {
    // a HTML i18n //i18n(ph="name") placeholdereket az Angular nagybetűsíti ({$VALUE}), a
    // $localize ${expr}:name: nevét viszont változatlanul hagyja - ezért KONVENCIÓ: a $localize
    // placeholder-nevek mindig NAGYBETŰSEK (${item.code}:CODE:), és itt minden nevet
    // nagybetűsítünk, hogy a fordító által beírt {{code}} / {$code} is illeszkedjen
    return text.replace(PARAM_PATTERN, (_match, mustacheName, localizeName) =>
      `{$${(mustacheName ?? localizeName).toUpperCase()}}`);
  }
}
